import { BrandPlan, BrandPlanContents, BrandPlanTactic, BrandPlanTacticContents } from 'types'

type TactictsWithoutContents =
  | BrandPlanTactic.SEO
  | BrandPlanTactic.Email
  | BrandPlanTactic.YouTube
  | BrandPlanTactic.TikTok
  | BrandPlanTactic.MetaAds
const tacticContentMap: Record<Exclude<BrandPlanTactic, TactictsWithoutContents>, keyof BrandPlan> = {
  [BrandPlanTactic.Instagram]: 'instagram_contents',
  [BrandPlanTactic.LinkedIn]: 'linkedin_contents',
  [BrandPlanTactic.Facebook]: 'facebook_contents',
  [BrandPlanTactic.Twitter]: 'twitter_contents',
  [BrandPlanTactic.InstagramReel]: 'instagram_contents',
}

export const getContentsCount = (contents: BrandPlanTacticContents) => {
  if (!contents) return 0

  return contents.reduce((total, item) => total + item.count, 0)
}

export const getTotalContentsCount = (...contentsArrays: BrandPlanTacticContents[]) => {
  if (!contentsArrays) return 0

  return contentsArrays.reduce((sum, contents) => sum + getContentsCount(contents), 0)
}

export const socialMediaTactics = (plan: Omit<BrandPlan, 'brand_profile'>) =>
  Object.keys(tacticContentMap).filter((tactic) => plan.tactics.includes(tactic as BrandPlanTactic))

const contentToTacticMap: Record<keyof BrandPlanContents, BrandPlanTactic> = {
  instagram_contents: BrandPlanTactic.Instagram,
  facebook_contents: BrandPlanTactic.Facebook,
  linkedin_contents: BrandPlanTactic.LinkedIn,
  twitter_contents: BrandPlanTactic.Twitter,
  youtube_contents: BrandPlanTactic.YouTube,
  tiktok_contents: BrandPlanTactic.TikTok,
  seo_contents: BrandPlanTactic.SEO,
  email_contents: BrandPlanTactic.Email,
  crosspost_contents: BrandPlanTactic.Instagram, // Not used for tactics derivation
  video_crosspost_contents: BrandPlanTactic.Instagram, // Not used for tactics derivation
}

/**
 * Derives tactics from content counts in a brand plan.
 * If a channel has content counts > 0, the corresponding tactic is added.
 * Special handling for InstagramReel: added if instagram_contents has items with type 'reel' and count > 0.
 */
export const getTacticsFromContentCounts = (contents: Partial<BrandPlanContents>): BrandPlanTactic[] => {
  const tactics: BrandPlanTactic[] = []

  // Map content fields to tactics
  Object.entries(contentToTacticMap).forEach(([contentField, tactic]) => {
    // Skip crosspost fields as they don't directly map to tactics
    if (contentField === 'crosspost_contents' || contentField === 'video_crosspost_contents') {
      return
    }

    const contentArray = contents[contentField as keyof BrandPlanContents]
    if (getContentsCount(contentArray as BrandPlanTacticContents) > 0) {
      tactics.push(tactic)
    }
  })

  // Special handling for InstagramReel (type 'reel' in instagram_contents)
  const hasReels = contents.instagram_contents?.some((item) => item.type === 'reel' && item.count > 0)
  if (hasReels) {
    tactics.push(BrandPlanTactic.InstagramReel)
  }

  return tactics
}

export const calculateAllBrandPlanTactics = (plan?: Omit<BrandPlan, 'brand_profile'>): BrandPlanTactic[] => {
  if (!plan) return []

  // Get tactics from content counts
  const tacticsFromContents = getTacticsFromContentCounts(plan)

  // Get existing tactics, filtering out SEO and Email
  const existingTactics = plan.tactics.filter(
    (tactic) => tactic !== BrandPlanTactic.SEO && tactic !== BrandPlanTactic.Email,
  )

  const mergedTactics = [...new Set([...tacticsFromContents, ...existingTactics])]

  return mergedTactics
}
